import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Check, ArrowRight, ArrowLeft, Rocket, Plus, X, MapPin } from "lucide-react";
import { serviceLabel, SERVICE_KEYS } from "@/lib/pricing";

const STEPS = ["Business", "Pricing", "Mesh", "Notifications", "Launch"];

export default function Onboarding() {
  const nav = useNavigate();
  const [company, setCompany] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/companies/me").then(({ data }) => setCompany(data)).catch(() => nav("/dashboard"));
  }, [nav]);

  if (!company) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/companies/me", patch);
      setCompany(data);
      return data;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    await save({ onboarding_completed: true });
    toast.success("You're live! Welcome to ScreenFlow.");
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2.5 mb-7">
          <div className="w-10 h-10 rounded-xl bg-[#0F172A] flex items-center justify-center text-white font-bold">S</div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Onboarding</div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Let's set up {company.business_name}</h1>
          </div>
        </div>

        {/* Step bar */}
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                i < step ? "bg-emerald-500 text-white" : i === step ? "bg-[#0F172A] text-white" : "bg-slate-200 text-slate-500"
              }`}>
                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
              </div>
              <span className={`text-xs font-medium hidden sm:inline ${i === step ? "text-slate-900" : "text-slate-500"}`}>{label}</span>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 rounded ${i < step ? "bg-emerald-500" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
          {step === 0 && <BusinessStep company={company} onSave={save} onNext={() => setStep(1)} />}
          {step === 1 && <PricingStep company={company} onSave={save} onNext={() => setStep(2)} onBack={() => setStep(0)} />}
          {step === 2 && <MeshStep company={company} onSave={save} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
          {step === 3 && <NotificationsStep company={company} onSave={save} onNext={() => setStep(4)} onBack={() => setStep(2)} />}
          {step === 4 && (
            <div className="text-center py-6 space-y-5">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <Rocket className="w-7 h-7" />
              </div>
              <h2 className="font-display text-2xl font-bold text-slate-900">You're live!</h2>
              <p className="text-slate-600">Share this link with customers to start collecting quotes:</p>
              <div className="inline-block bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 font-mono text-sm text-slate-800 break-all">
                /{company.slug}/quote
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-3">
                <Button variant="outline" onClick={() => setStep(3)} className="rounded-xl">
                  <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button data-testid="onboarding-finish-btn" onClick={finish} disabled={saving} className="bg-[#0F172A] hover:bg-black rounded-xl">
                  Open my dashboard <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BusinessStep({ company, onSave, onNext }) {
  const [info, setInfo] = useState(company.business_info || {});
  const [name, setName] = useState(company.business_name);
  const [zips, setZips] = useState(() => {
    const existing = (company.marketplace && company.marketplace.custom_zips) || [];
    return Array.isArray(existing) ? existing.filter((z) => /^\d{5}$/.test(String(z))) : [];
  });
  const [zipInput, setZipInput] = useState("");
  const [zipError, setZipError] = useState("");
  const [savingZips, setSavingZips] = useState(false);

  const addZips = (rawValue) => {
    setZipError("");
    const raw = (rawValue ?? zipInput).trim();
    if (!raw) return;
    // Accept comma/space/newline separated batch entry. Strict 5-digit validation.
    const tokens = raw.split(/[\s,]+/).map((z) => z.trim()).filter(Boolean);
    const valid = [];
    const invalid = [];
    for (const t of tokens) {
      if (/^\d{5}$/.test(t)) valid.push(t);
      else invalid.push(t);
    }
    if (invalid.length) {
      setZipError(`Not a valid 5-digit ZIP: ${invalid.slice(0, 3).join(", ")}`);
    }
    if (valid.length) {
      setZips((prev) => Array.from(new Set([...prev, ...valid])));
      setZipInput("");
    }
  };

  const removeZip = (zip) => setZips((prev) => prev.filter((z) => z !== zip));

  const onZipKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "," || e.key === " " || e.key === "Tab") {
      if (zipInput.trim()) {
        e.preventDefault();
        addZips();
      }
    }
  };

  const next = async () => {
    if (zips.length === 0) {
      setZipError("Add at least one ZIP code so we know where to send leads.");
      return;
    }
    setSavingZips(true);
    try {
      // Persist ONLY to marketplace routing — `business_info.service_area` is
      // a homeowner-facing tagline (rendered on the public funnel header) and
      // should never contain raw ZIPs. Leave it untouched; the owner can set
      // their own tagline from Settings → Business → Service area.
      await onSave({ business_name: name, business_info: info });
      // Save ZIPs to marketplace routing settings via dedicated endpoint
      await api.put("/marketplace/settings", { custom_zips: zips });
      onNext();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save service area");
    } finally {
      setSavingZips(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-900">Your business</h2>
        <p className="text-sm text-slate-500 mt-1">Shows on your customer-facing quote page.</p>
      </div>
      <FieldRow label="Company name">
        <Input data-testid="onb-business-name" className="h-11 rounded-xl" value={name} onChange={(e) => setName(e.target.value)} />
      </FieldRow>
      <FieldRow label="Business phone">
        <Input data-testid="onb-phone" className="h-11 rounded-xl" placeholder="(555) 555-1234" value={info.phone || ""} onChange={(e) => setInfo({ ...info, phone: e.target.value })} />
      </FieldRow>

      {/* ZIP code collector — replaces the old single-line "service area" text field */}
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Service area — ZIP codes</Label>
        <p className="text-xs text-slate-500 mt-1 mb-2 leading-relaxed">
          Add every ZIP you service. These power your <strong>marketplace lead routing</strong> — homeowners in
          these ZIPs can be routed straight to your dashboard.
        </p>

        <div className="flex gap-2">
          <Input
            data-testid="onb-zip-input"
            inputMode="numeric"
            maxLength={64}
            className="h-11 rounded-xl flex-1"
            placeholder="e.g., 92562, 92563, 92591"
            value={zipInput}
            onChange={(e) => { setZipError(""); setZipInput(e.target.value); }}
            onKeyDown={onZipKeyDown}
            onBlur={() => zipInput.trim() && addZips()}
          />
          <Button
            type="button"
            data-testid="onb-zip-add-btn"
            onClick={() => addZips()}
            variant="outline"
            className="h-11 rounded-xl px-4"
          >
            <Plus className="w-4 h-4 mr-1" /> Add
          </Button>
        </div>
        {zipError && (
          <p data-testid="onb-zip-error" className="text-xs text-red-600 mt-1.5">{zipError}</p>
        )}
        <p className="text-[11px] text-slate-400 mt-1.5">Press Enter, comma, or space to add multiple ZIPs at once.</p>

        {zips.length > 0 && (
          <div data-testid="onb-zip-list" className="mt-3 flex flex-wrap gap-1.5">
            {zips.map((z) => (
              <span
                key={z}
                data-testid={`onb-zip-chip-${z}`}
                className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 transition-colors text-slate-800 text-xs font-mono rounded-full pl-2.5 pr-1 py-1"
              >
                <MapPin className="w-3 h-3 text-slate-500" />
                {z}
                <button
                  type="button"
                  data-testid={`onb-zip-remove-${z}`}
                  onClick={() => removeZip(z)}
                  className="ml-0.5 w-4 h-4 rounded-full hover:bg-slate-300 flex items-center justify-center"
                  aria-label={`Remove ${z}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="mt-3 rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800 leading-relaxed">
          <strong>Why ZIPs matter:</strong> Marketplace leads are routed by ZIP. Companies with more ZIPs covered
          win more leads — especially in competitive metros. You can refine these anytime in
          <em> Settings → Marketplace</em>.
        </div>
      </div>

      <div className="flex justify-end pt-3">
        <Button data-testid="onb-business-next" onClick={next} disabled={savingZips} className="bg-[#0F172A] hover:bg-black rounded-xl h-11">
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function PricingStep({ company, onSave, onNext, onBack }) {
  const [settings, setSettings] = useState(company.settings);

  const next = async () => {
    await onSave({ settings });
    onNext();
  };

  const setSvc = (key, patch) => {
    setSettings({ ...settings, services: { ...settings.services, [key]: { ...settings.services[key], ...patch } } });
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-900">Pricing</h2>
        <p className="text-sm text-slate-500 mt-1">Set per-screen pricing. You can change this anytime.</p>
      </div>

      <div className="space-y-3">
        {SERVICE_KEYS.map((k) => (
          <div key={k} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
            <Switch data-testid={`onb-svc-${k}-toggle`} checked={settings.services[k].enabled} onCheckedChange={(v) => setSvc(k, { enabled: v })} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{serviceLabel(k)}</div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-slate-500">$</span>
              <Input
                data-testid={`onb-svc-${k}-price`}
                type="number"
                value={settings.services[k].price_per_unit}
                onChange={(e) => setSvc(k, { price_per_unit: parseFloat(e.target.value) || 0 })}
                className="h-10 w-24 rounded-lg text-right"
              />
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <FieldRow label="Minimum service fee">
          <div className="flex items-center gap-1">
            <span className="text-slate-500">$</span>
            <Input data-testid="onb-min-fee" type="number" value={settings.minimum_service_fee} onChange={(e) => setSettings({ ...settings, minimum_service_fee: parseFloat(e.target.value) || 0 })} className="h-11 rounded-xl" />
          </div>
        </FieldRow>
        <FieldRow label="Trip fee">
          <div className="flex items-center gap-1">
            <span className="text-slate-500">$</span>
            <Input type="number" value={settings.trip_fee} onChange={(e) => setSettings({ ...settings, trip_fee: parseFloat(e.target.value) || 0 })} className="h-11 rounded-xl" />
          </div>
        </FieldRow>
      </div>

      <div className="flex justify-between pt-3">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-11">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button data-testid="onb-pricing-next" onClick={next} className="bg-[#0F172A] hover:bg-black rounded-xl h-11">
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function MeshStep({ company, onSave, onNext, onBack }) {
  const [mesh, setMesh] = useState(company.settings.mesh_upgrades || []);

  const next = async () => {
    await onSave({ settings: { ...company.settings, mesh_upgrades: mesh } });
    onNext();
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-900">Mesh upgrades</h2>
        <p className="text-sm text-slate-500 mt-1">Optional add-ons customers can pick during their quote. Adjust pricing or turn off any option.</p>
      </div>

      <div className="space-y-3">
        {mesh.map((m, i) => (
          <div key={m.id} className="rounded-xl border border-slate-200 p-3 space-y-2">
            <div className="flex items-center gap-3">
              <Switch data-testid={`onb-mesh-${i}-toggle`} checked={m.enabled} onCheckedChange={(v) => setMesh(mesh.map((x, j) => j === i ? { ...x, enabled: v } : x))} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900">{m.name} {m.is_default && <span className="text-[10px] uppercase tracking-widest text-emerald-600 ml-1.5">Default</span>}</div>
                <div className="text-xs text-slate-500 truncate">{m.description}</div>
              </div>
            </div>
            {!m.is_default && (
              <div className="grid grid-cols-2 gap-2 pl-11">
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500 w-20">Window +$</span>
                  <Input data-testid={`onb-mesh-${i}-price`} type="number" value={m.price_per_screen} onChange={(e) => setMesh(mesh.map((x, j) => j === i ? { ...x, price_per_screen: parseFloat(e.target.value) || 0 } : x))} className="h-9 rounded-lg text-right text-sm" />
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-xs text-slate-500 w-20">Door +$</span>
                  <Input data-testid={`onb-mesh-${i}-door-price`} type="number" value={m.door_price_per_screen ?? 0} onChange={(e) => setMesh(mesh.map((x, j) => j === i ? { ...x, door_price_per_screen: parseFloat(e.target.value) || 0 } : x))} className="h-9 rounded-lg text-right text-sm" />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex justify-between pt-3">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-11">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button data-testid="onb-mesh-next" onClick={next} className="bg-[#0F172A] hover:bg-black rounded-xl h-11">
          Continue <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function NotificationsStep({ company, onSave, onNext, onBack }) {
  const [info, setInfo] = useState(company.business_info || {});

  const next = async () => {
    await onSave({ business_info: info });
    onNext();
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold text-slate-900">Email notifications</h2>
        <p className="text-sm text-slate-500 mt-1">Where should we email you when a new quote comes in?</p>
      </div>
      <FieldRow label="Notification email">
        <Input
          data-testid="onb-notification-email"
          type="email"
          className="h-11 rounded-xl"
          placeholder="quotes@yourcompany.com"
          value={info.email || ""}
          onChange={(e) => setInfo({ ...info, email: e.target.value })}
        />
      </FieldRow>
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
        Every time a customer completes a quote, we'll email this address instantly so you can follow up while their interest is hot.
      </div>
      <div className="flex justify-between pt-3">
        <Button variant="outline" onClick={onBack} className="rounded-xl h-11">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <Button data-testid="onb-notifications-next" onClick={next} className="bg-[#0F172A] hover:bg-black rounded-xl h-11">
          Launch <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
